import { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, Button, Card } from './ui'
import { useAuthStore } from '../stores/authStore'
import useDraftStore from '../stores/draftStore'
import api from '../services/api'

export default function ImportProgramsModal({ isOpen, onClose, currentClinicId, onImport }) {
  const [availableClinics, setAvailableClinics] = useState([])
  const [selectedClinicId, setSelectedClinicId] = useState(null)
  const [programs, setPrograms] = useState([])
  const [selectedPrograms, setSelectedPrograms] = useState(new Set())
  const [loading, setLoading] = useState(false)
  const [importing, setImporting] = useState(false)
  const [error, setError] = useState(null)
  
  const { getAvailableClinics } = useAuthStore()
  const { addChange } = useDraftStore()

  // Load available clinics when modal opens
  useEffect(() => {
    if (isOpen) {
      loadAvailableClinics()
    }
  }, [isOpen])

  // Load programs when a clinic is selected
  useEffect(() => {
    if (selectedClinicId) {
      loadPrograms(selectedClinicId)
    }
  }, [selectedClinicId])

  const loadAvailableClinics = () => {
    try {
      console.log('[IMPORT PROGRAMS] Loading available clinics...')
      
      // Get all clinics the user has access to from auth store
      const allClinics = getAvailableClinics()
      console.log('[IMPORT PROGRAMS] All clinics from auth store:', allClinics)
      console.log('[IMPORT PROGRAMS] Current clinic ID:', currentClinicId)
      
      // Show all clinics (including current) — current clinic first
      const sorted = [...allClinics].sort((a, b) => {
        if (a.id === currentClinicId) return -1
        if (b.id === currentClinicId) return 1
        return 0
      })
      setAvailableClinics(sorted)
      
      console.log('[IMPORT PROGRAMS] Available clinics for import:', sorted)
    } catch (error) {
      console.error('[IMPORT PROGRAMS] Error loading clinics:', error)
      setAvailableClinics([])
    }
  }

  const loadPrograms = async (clinicId) => {
    try {
      setLoading(true)
      setError(null)
      
      console.log('[IMPORT PROGRAMS] Starting to load programs from clinic:', clinicId)
      console.log('[IMPORT PROGRAMS] Current clinic ID:', currentClinicId)
      
      // Use source_clinic query parameter to fetch from a different clinic
      // Skip source_clinic when fetching from current clinic (already on that DB)
      const params = { limit: 1000, include_inactive: false }
      if (clinicId !== currentClinicId) {
        params.source_clinic = clinicId
      }
      const response = await api.get('/drug-programs', { params })
      
      console.log('[IMPORT PROGRAMS] API Response status:', response.status)
      console.log('[IMPORT PROGRAMS] API Response data:', response.data)
      console.log('[IMPORT PROGRAMS] Programs array:', response.data.programs)
      console.log('[IMPORT PROGRAMS] Number of programs:', response.data.programs?.length || 0)
      
      const programsList = response.data.programs || []
      setPrograms(programsList)
      setSelectedPrograms(new Set())
      
      console.log('[IMPORT PROGRAMS] State updated with', programsList.length, 'programs')
    } catch (error) {
      console.error('[IMPORT PROGRAMS] Error loading programs:', error)
      console.error('[IMPORT PROGRAMS] Error response:', error.response)
      console.error('[IMPORT PROGRAMS] Error status:', error.response?.status)
      console.error('[IMPORT PROGRAMS] Error data:', error.response?.data)
      
      const errorMsg = error.response?.status === 404 
        ? `Clinic "${clinicId}" not found or has no drug program data available.`
        : error.response?.status === 403
        ? `You don't have permission to access clinic "${clinicId}".`
        : error.response?.data?.message || 'Failed to load programs from selected clinic.'
      setError(errorMsg)
      setPrograms([])
    } finally {
      setLoading(false)
    }
  }

  const toggleProgramSelection = (programId) => {
    const newSelection = new Set(selectedPrograms)
    if (newSelection.has(programId)) {
      newSelection.delete(programId)
    } else {
      newSelection.add(programId)
    }
    setSelectedPrograms(newSelection)
  }

  const toggleSelectAll = () => {
    if (selectedPrograms.size === programs.length) {
      setSelectedPrograms(new Set())
    } else {
      setSelectedPrograms(new Set(programs.map(p => p._id)))
    }
  }

  const handleImport = () => {
    if (selectedPrograms.size === 0) return
    
    try {
      setImporting(true)
      
      // Get selected program data
      const programsToImport = programs.filter(p => selectedPrograms.has(p._id))
      
      // Helper to convert string booleans to actual booleans
      const cleanBooleanValue = (value) => {
        if (typeof value === 'string') {
          if (value.toLowerCase() === 'true') return true
          if (value.toLowerCase() === 'false') return false
        }
        return value
      }
      
      // Recursively clean an object
      const cleanObject = (obj) => {
        if (Array.isArray(obj)) {
          return obj.map(item => cleanObject(item))
        } else if (obj !== null && typeof obj === 'object') {
          const cleaned = {}
          for (const [key, value] of Object.entries(obj)) {
            cleaned[key] = cleanObject(value)
          }
          return cleaned
        } else {
          return cleanBooleanValue(obj)
        }
      }
      
      // Build a set of existing entity_ids when importing from same clinic
      const isSameClinic = selectedClinicId === currentClinicId
      const existingIds = isSameClinic
        ? new Set(programs.map(p => p.entity_id))
        : new Set()

      // Add each program as a draft change
      programsToImport.forEach(program => {
        // Remove _id and clinic-specific fields for import
        const { _id, created_by, updated_by, created_at, updated_at, ...programData } = program

        // Clean the data to convert string booleans to actual booleans
        const cleanedData = cleanObject(programData)

        // For same-clinic imports, generate a unique entity_id to avoid conflicts
        if (isSameClinic) {
          let newId = cleanedData.entity_id + '_copy'
          let n = 2
          while (existingIds.has(newId)) {
            newId = cleanedData.entity_id + `_copy${n}`
            n++
          }
          existingIds.add(newId)
          cleanedData.entity_id = newId
          cleanedData.drug_name = cleanedData.drug_name + ' (Copy)'
        }

        addChange({
          resource_type: 'drug_program',
          resource_id: cleanedData.entity_id,
          operation: 'create',
          original_data: null,
          new_data: cleanedData,
          description: `Import: ${cleanedData.drug_name} (${cleanedData.entity_id}) - ${cleanedData.pharmacies?.length || 0} pharmacies configured`
        })
      })
      
      console.log(`[IMPORT] Added ${programsToImport.length} programs to drafts`)
      
      // Notify parent and close modal
      onImport?.()
      onClose()
      
      // Reset state
      setSelectedClinicId(null)
      setPrograms([])
      setSelectedPrograms(new Set())
    } catch (error) {
      console.error('[IMPORT] Error importing programs:', error)
      setError('Failed to import programs. Please try again.')
    } finally {
      setImporting(false)
    }
  }

  const handleClose = () => {
    // Reset state on close
    setSelectedClinicId(null)
    setPrograms([])
    setSelectedPrograms(new Set())
    setError(null)
    onClose()
  }

  if (!isOpen) return null

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Import Drug Programs</DialogTitle>
          <DialogDescription>
            Select programs from any clinic to import. Programs will be added to your drafts.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4">
          {/* Clinic Selection */}
          <div>
            <label className="block text-sm font-medium mb-2">Select Source Clinic</label>
            {availableClinics.length === 0 ? (
              <div className="text-center text-muted-foreground py-8 border rounded-lg bg-muted/20">
                No clinics available for import
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {availableClinics.map(clinic => (
                  <Button
                    key={clinic.id}
                    variant={selectedClinicId === clinic.id ? 'default' : 'outline'}
                    className="justify-start h-auto py-3"
                    onClick={() => setSelectedClinicId(clinic.id)}
                  >
                    <div className="text-left">
                      <div className="font-medium">
                        {clinic.name}
                        {clinic.id === currentClinicId && (
                          <span className="ml-1.5 text-xs font-normal text-muted-foreground">(current)</span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">{clinic.id}</div>
                    </div>
                  </Button>
                ))}
              </div>
            )}
          </div>

          {/* Programs List */}
          {selectedClinicId && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium">
                  Available Programs ({programs.length})
                </label>
                {programs.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={toggleSelectAll}
                  >
                    {selectedPrograms.size === programs.length ? 'Deselect All' : 'Select All'}
                  </Button>
                )}
              </div>

              {loading ? (
                <div className="text-center text-muted-foreground py-8">
                  Loading programs...
                </div>
              ) : error ? (
                <div className="p-8 text-center">
                  <div className="text-destructive font-medium mb-2">Error Loading Programs</div>
                  <div className="text-sm text-muted-foreground">{error}</div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="mt-4"
                    onClick={() => loadPrograms(selectedClinicId)}
                  >
                    Try Again
                  </Button>
                </div>
              ) : programs.length === 0 ? (
                <div className="text-center text-muted-foreground py-8 border rounded-lg bg-muted/20">
                  No programs found in selected clinic
                </div>
              ) : (
                <div className="border rounded-lg overflow-hidden max-h-96 overflow-y-auto">
                  <table className="w-full">
                    <thead className="bg-muted/50 sticky top-0">
                      <tr>
                        <th className="w-12 p-2">
                          <input
                            type="checkbox"
                            checked={selectedPrograms.size === programs.length}
                            onChange={toggleSelectAll}
                            className="cursor-pointer"
                          />
                        </th>
                        <th className="text-left p-2 font-medium">Drug Name</th>
                        <th className="text-left p-2 font-medium">Type</th>
                        <th className="text-left p-2 font-medium">Pharmacies</th>
                      </tr>
                    </thead>
                    <tbody>
                      {programs.map((program) => (
                        <tr
                          key={program._id}
                          className={`border-t hover:bg-muted/30 cursor-pointer ${
                            selectedPrograms.has(program._id) ? 'bg-primary/10' : ''
                          }`}
                          onClick={() => toggleProgramSelection(program._id)}
                        >
                          <td className="p-2">
                            <input
                              type="checkbox"
                              checked={selectedPrograms.has(program._id)}
                              onChange={() => toggleProgramSelection(program._id)}
                              className="cursor-pointer"
                            />
                          </td>
                          <td className="p-2 font-medium">{program.drug_name}</td>
                          <td className="p-2 text-sm text-muted-foreground">
                            {program.program_type || 'N/A'}
                          </td>
                          <td className="p-2 text-sm text-muted-foreground">
                            {program.pharmacies?.length || 0} pharmacies
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center pt-4 border-t">
          <div className="text-sm text-muted-foreground">
            {selectedPrograms.size > 0 && (
              <span>{selectedPrograms.size} program{selectedPrograms.size !== 1 ? 's' : ''} selected</span>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleClose} disabled={importing}>
              Cancel
            </Button>
            <Button
              onClick={handleImport}
              disabled={selectedPrograms.size === 0 || importing}
            >
              {importing ? 'Importing...' : `Import ${selectedPrograms.size} Program${selectedPrograms.size !== 1 ? 's' : ''}`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

